import * as React from "react";
import {
  Document,
  Page,
  View,
  Text,
  StyleSheet,
  pdf,
} from "@react-pdf/renderer";
import type { Style } from "@react-pdf/types";
import type { ColumnDef } from "../columns";
import { resolveAccessor } from "../columns";
import { formatCell, type FormatterBundle } from "../lib/format";

/**
 * MIME type used for PDF blobs.
 */
export const PDF_MIME_TYPE = "application/pdf";

/**
 * Width-in-points of A4 landscape minus the page padding (24pt each side).
 * react-pdf uses points (72 per inch) as its default unit.
 *
 * A4 landscape: 842 × 595 pt → usable width 842 - 48 = 794pt.
 */
const A4_LANDSCAPE_USABLE_WIDTH = 794;

/**
 * Static color tokens. We can't import the runtime tokens directly because
 * @react-pdf styles need fully-resolved colors at stylesheet construction
 * time, not CSS variables. The values match the `light` palette from
 * `@aio/platform-tokens` so PDFs feel of-a-piece with the on-screen table.
 *
 * If `@aio/platform-tokens` ever changes these defaults the constants here
 * need to follow; the duplication is intentional and called out.
 */
const TOKENS = {
  fg: "#1F2937", // matches platform fg (slate-800-ish)
  fgMuted: "#525E77",
  border: "#E5E7EB",
  bgSubtle: "#F1F5F9",
  bgElevated: "#FFFFFF",
};

/**
 * Stylesheet for the table layout. @react-pdf's `StyleSheet.create` does no
 * runtime CSS-in-JS — the styles are baked into the document at render time.
 */
const styles = StyleSheet.create({
  page: {
    padding: 24,
    fontSize: 9,
    color: TOKENS.fg,
    fontFamily: "Helvetica",
  },
  title: {
    fontSize: 14,
    marginBottom: 4,
    fontFamily: "Helvetica-Bold",
  },
  subtitle: {
    fontSize: 9,
    color: TOKENS.fgMuted,
    marginBottom: 12,
  },
  table: {
    width: "100%",
    borderWidth: 1,
    borderColor: TOKENS.border,
    borderStyle: "solid",
  },
  headerRow: {
    flexDirection: "row",
    backgroundColor: TOKENS.bgSubtle,
    borderBottomWidth: 1,
    borderBottomColor: TOKENS.border,
    borderBottomStyle: "solid",
  },
  headerCell: {
    padding: 6,
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
  },
  row: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: TOKENS.border,
    borderBottomStyle: "solid",
  },
  rowZebra: {
    backgroundColor: TOKENS.bgElevated,
  },
  rowZebraAlt: {
    backgroundColor: TOKENS.bgSubtle,
  },
  cell: {
    padding: 6,
    fontSize: 9,
  },
  cellNumeric: {
    fontFamily: "Courier", // monospace for aligned numerics
    textAlign: "right",
  },
  footer: {
    position: "absolute",
    bottom: 12,
    left: 24,
    right: 24,
    flexDirection: "row",
    justifyContent: "space-between",
    fontSize: 8,
    color: TOKENS.fgMuted,
  },
});

/**
 * Compute per-column widths in points.
 *
 * Resolution order:
 * 1. If every column declares a numeric `width` (px), normalise them into
 *    the available A4-landscape usable width by their relative ratio.
 * 2. Otherwise, give every column an equal slice of the usable width.
 *
 * This mirrors the table-on-screen behaviour where explicit widths win and
 * everything else divides the remainder.
 */
function computeColumnWidths<TRow>(columns: ColumnDef<TRow>[]): number[] {
  if (columns.length === 0) return [];
  const totalDeclared = columns.reduce((acc, c) => acc + (c.width ?? 0), 0);
  if (totalDeclared > 0 && columns.every((c) => typeof c.width === "number")) {
    return columns.map(
      (c) => ((c.width as number) / totalDeclared) * A4_LANDSCAPE_USABLE_WIDTH,
    );
  }
  const equal = A4_LANDSCAPE_USABLE_WIDTH / columns.length;
  return columns.map(() => equal);
}

/**
 * Decide whether a column should render with a monospaced numeric style.
 * Numeric / money / percent columns get the monospaced + right-aligned
 * treatment so columns of figures line up cleanly under the header.
 */
function isNumeric<TRow>(column: ColumnDef<TRow>): boolean {
  return (
    column.type === "number" ||
    column.type === "money" ||
    column.type === "percent"
  );
}

/**
 * Typed factories for the PDF primitives. `Text` and `View` are exported with
 * polymorphic prop types (PDF-mode vs SVG-mode); using the exported component
 * directly with `createElement` makes TypeScript pick the SVG variant when
 * the style includes `width`, which fails. Wrapping in typed factories pins
 * the PDF-mode prop shape so the style accepts standard layout attributes.
 */
type PdfTextProps = {
  key?: string;
  style?: Style | Style[];
  fixed?: boolean;
  wrap?: boolean;
  render?: (props: {
    pageNumber: number;
    totalPages: number;
    subPageNumber: number;
    subPageTotalPages: number;
  }) => React.ReactNode;
  children?: React.ReactNode;
};

type PdfViewProps = {
  key?: string;
  style?: Style | Style[];
  fixed?: boolean;
  wrap?: boolean;
  children?: React.ReactNode;
};

const PdfText = Text as unknown as React.ComponentType<PdfTextProps>;
const PdfView = View as unknown as React.ComponentType<PdfViewProps>;

/**
 * Build the PDF document tree. We use `React.createElement` (not JSX) so this
 * file stays `.ts` and the table package's compile pipeline does not have to
 * grow a separate JSX-aware path for one component.
 */
function buildDocument<TRow>(opts: {
  rows: TRow[];
  columns: ColumnDef<TRow>[];
  locale: string;
  formatters: FormatterBundle;
  title: string;
  subtitle: string | undefined;
  widths: number[];
}): React.ReactElement {
  const { rows, columns, locale, formatters, title, subtitle, widths } = opts;

  const headerCells = columns.map((column, idx) => {
    // computeColumnWidths always returns a width for every column, but TS
    // sees `widths[idx]` as `number | undefined` under noUncheckedIndexedAccess.
    // Resolve once with a deterministic fallback (equal-share).
    const width = widths[idx] ?? A4_LANDSCAPE_USABLE_WIDTH / columns.length;
    return React.createElement(
      PdfText,
      {
        key: column.key,
        style: {
          ...styles.headerCell,
          width,
          textAlign: isNumeric(column) ? "right" : "left",
        },
      },
      column.header,
    );
  });

  const headerRow = React.createElement(
    PdfView,
    { style: styles.headerRow, fixed: true, key: "header" },
    headerCells,
  );

  const bodyRows = rows.map((row, rowIdx) => {
    const cells = columns.map((column, colIdx) => {
      const accessor = resolveAccessor(column);
      const value = accessor(row);
      const display = formatCell(value, column, locale, formatters, { row });
      const width = widths[colIdx] ?? A4_LANDSCAPE_USABLE_WIDTH / columns.length;
      return React.createElement(
        PdfText,
        {
          key: column.key,
          style: {
            ...styles.cell,
            ...(isNumeric(column) ? styles.cellNumeric : {}),
            width,
            textAlign:
              column.align ?? (isNumeric(column) ? "right" : "left"),
          },
        },
        display,
      );
    });
    return React.createElement(
      PdfView,
      {
        // Index keys are fine here — rows are immutable for the lifetime of
        // the PDF render and there's no reordering to mishandle.
        key: `row-${rowIdx}`,
        style: {
          ...styles.row,
          ...(rowIdx % 2 === 0 ? styles.rowZebra : styles.rowZebraAlt),
        },
        wrap: false,
      },
      cells,
    );
  });

  const tableEl = React.createElement(
    PdfView,
    { style: styles.table, key: "table" },
    [headerRow, ...bodyRows],
  );

  const footerEl = React.createElement(
    PdfView,
    { style: styles.footer, fixed: true, key: "footer" },
    [
      React.createElement(PdfText, { key: "footer-title" }, title),
      React.createElement(PdfText, {
        key: "footer-pages",
        render: ({
          pageNumber,
          totalPages,
        }: {
          pageNumber: number;
          totalPages: number;
        }) => `Page ${pageNumber} of ${totalPages}`,
      }),
    ],
  );

  const titleEl = React.createElement(
    PdfText,
    { style: styles.title, key: "title" },
    title,
  );
  const subtitleEl = subtitle
    ? React.createElement(
        PdfText,
        { style: styles.subtitle, key: "subtitle" },
        subtitle,
      )
    : null;

  const pageChildren: React.ReactNode[] = [titleEl];
  if (subtitleEl) pageChildren.push(subtitleEl);
  pageChildren.push(tableEl, footerEl);

  // Page is typed with a polymorphic `children` slot but TS infers
  // `unknown` props for `pageEl` once it reaches the Document.children
  // position under exactOptionalPropertyTypes. Wrap in a typed factory the
  // same way we did for Text/View so Document accepts it cleanly.
  const PdfPage = Page as unknown as React.ComponentType<{
    size?: string;
    orientation?: "landscape" | "portrait";
    style?: Style | Style[];
    children?: React.ReactNode;
  }>;

  const pageEl = React.createElement(
    PdfPage,
    {
      size: "A4",
      orientation: "landscape",
      style: styles.page,
    },
    pageChildren,
  );

  const PdfDocument = Document as unknown as React.ComponentType<{
    title?: string;
    producer?: string;
    creator?: string;
    children?: React.ReactNode;
  }>;

  return React.createElement(
    PdfDocument,
    { title, producer: "AIO", creator: "AIO Table Engine" },
    pageEl,
  );
}

/**
 * Options accepted by {@link toPdf}.
 */
export interface ToPdfOptions<TRow> {
  /** Row data in display order (sort/filter already applied by caller). */
  rows: TRow[];
  /** Column definitions. Hidden columns must be omitted by the caller. */
  columns: ColumnDef<TRow>[];
  /** Locale tag passed to formatters (e.g. "sr-Latn", "en-GB"). */
  locale: string;
  /** Formatter bundle resolved from `@aio/platform-i18n` or test mocks. */
  formatters: FormatterBundle;
  /** Document title rendered above the table. Defaults to "Export". */
  title?: string;
  /**
   * Optional subtitle rendered below the title (e.g. "Generated 2026-05-02 ·
   * 142 rows · filtered by status=paid"). Caller controls localisation.
   */
  subtitle?: string;
}

/**
 * Serialize rows to a PDF blob.
 *
 * The layout is intentionally simple: title + optional subtitle, header row,
 * zebra-striped body rows, page-number footer. Numeric / money / percent
 * columns render in a monospaced face for column alignment.
 *
 * Caveats:
 * - Each row is `wrap={false}` — wide tables that don't fit will truncate
 *   horizontally rather than wrap onto multiple lines. The right answer for
 *   very wide tables is a "landscape A3" toggle; out of scope here.
 * - @react-pdf only ships Helvetica / Courier / Times by default. Custom
 *   fonts (e.g. for Cyrillic) need `Font.register({ src: ... })` on the
 *   caller side. We document this here rather than auto-registering anything
 *   — bundling fonts would silently increase package weight.
 */
export async function toPdf<TRow>(options: ToPdfOptions<TRow>): Promise<Blob> {
  const {
    rows,
    columns,
    locale,
    formatters,
    title = "Export",
    subtitle,
  } = options;

  const widths = computeColumnWidths(columns);
  const doc = buildDocument({
    rows,
    columns,
    locale,
    formatters,
    title,
    subtitle,
    widths,
  });

  // `pdf(doc).toBlob()` runs entirely client-side; no Node fs / canvas APIs
  // are touched, so it works inside browsers. In jsdom-based tests we still
  // use this surface — the renderer falls back to a polyfill stream.
  //
  // The double-cast bridges TS's strict prop-type tracking — `pdf()` expects
  // `ReactElement<DocumentProps>`, our typed-factory wrapper produced
  // `ReactElement<unknown>`. Re-narrowing through the renderer-typed Document
  // is the smallest workaround that keeps the rest of the function clean.
  const pdfArg = doc as unknown as Parameters<typeof pdf>[0];
  const blob = await pdf(pdfArg).toBlob();
  // Normalise the MIME type — @react-pdf historically returned blobs with no
  // type set, which breaks `<a download>` heuristics in some browsers.
  if (blob.type === PDF_MIME_TYPE) return blob;
  return new Blob([blob], { type: PDF_MIME_TYPE });
}
